import React, { useState } from "react";
import { Tab, Row, Col, Nav } from "react-bootstrap";
import QuizLandingPage from "./QuizLandingPage";
import QuizActivePage from "./QuizActivePage";
import { IconCheck, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";

const QuizDashboard = ({
  allQuizzes = [],
  onAnswerSelect,
  onFinalSubmit,
}) => {
  const [isQuizStarted, setIsQuizStarted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const totalPages = Math.ceil(allQuizzes.length / itemsPerPage);

  const paginatedQuizzes = allQuizzes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getQuizId = (quiz) => quiz.id || quiz.question_set_id;

  if (allQuizzes.length === 0) {
    return (
      <div className="container-xl mt-5 text-center">
        <div className="empty">
          <div className="empty-img">
            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-clipboard-off" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <path d="M5.575 5.597a2 2 0 0 0 -.575 1.403v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2m0 -4v-8a2 2 0 0 0 -2 -2h-2"></path>
              <path d="M9 11h6"></path><path d="M9 15h4"></path>
              <path d="M12 3a1 1 0 0 1 1 1v1a1 1 0 0 1 -1 1a1 1 0 0 1 -1 -1v-1a1 1 0 0 1 1 -1z"></path>
              <path d="M3 3l18 18"></path>
            </svg>
          </div>
          <p className="empty-title">No assessments found</p>
          <p className="empty-subtitle text-muted">No assessments assigned to your account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-body mt-0 p-0">
      <style>
        {`
          .quiz-nav-link {
            border: 1px solid transparent !important;
            color: #616876 !important;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .quiz-nav-link:hover {
            background-color: rgba(17, 103, 232, 0.05) !important;
            color: #1167e8 !important;
          }
          .quiz-nav-link.active {
            background-color: #e7f1ff !important;
            color: #1167e8 !important;
            font-weight: 700 !important;
            border-left: 4px solid #1167e8 !important;
            border-radius: 4px 12px 12px 4px !important;
          }
          .quiz-nav-link.active .title-label {
            color: #0a1628 !important;
          }
          .animate-fade-in {
            animation: fadeIn 0.4s ease-out;
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
      {/* FULL-WIDTH HEADER */}
      <div 
        className="page-header mt-0 py-5 text-white mb-0" 
        style={{ 
          background: 'linear-gradient(135deg, #0a1628 0%, #1167e8 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.1)' 
        }}
      >
        <div className="container-xl">
          <div className="row align-items-center">
            <div className="col">
              <h1 className="fw-bold text-white mb-2" style={{ fontSize: '1.75rem' }}>My Assessments</h1>
              <div className="opacity-75 fw-medium">Manage and track your quiz progress</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-xl py-5">
        <div className="card shadow-lg border-0 rounded-4 overflow-hidden">
          <div className="card-body p-0">
            <Tab.Container
              id="quiz-tabs"
              defaultActiveKey={getQuizId(allQuizzes[0])}
              onSelect={() => setIsQuizStarted(false)}
            >
              <Row className="g-0">
                {/* LEFT SIDEBAR */}
                <Col
                  sm={3}
                  className="border card shadow-lg d-flex flex-column"
                  style={{ minHeight: "650px", backgroundColor: "#fcfdfe" }}
                >
                  <div className="p-4 border-bottom bg-white">
                    <h5 className="mb-0 fw-bold d-flex align-items-center gap-2 text-dark">
                       <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-list mr-2" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M9 6l11 0"></path><path d="M9 12l11 0"></path><path d="M9 18l11 0"></path>
                        <path d="M5 6l0 .01"></path><path d="M5 12l0 .01"></path><path d="M5 18l0 .01"></path>
                       </svg>
                       Assessment List
                    </h5>
                  </div>

                  <div className="flex-grow-1 overflow-auto">
                    <Nav className="flex-column p-3">
                      {paginatedQuizzes.map((quiz) => (
                        <Nav.Item key={getQuizId(quiz)} className="mb-2">
                          <Nav.Link
                            eventKey={getQuizId(quiz)}
                            className="p-3 border-0 transition-all rounded-3 quiz-nav-link shadow-none"
                          >
                            <div className="text-truncate">
                              <div className="fw-bold mb-2 title-label" style={{ fontSize: '0.9rem' }}>
                                {quiz.title}
                              </div>
                              <span
                                className={`badge ${
                                  quiz.status === "completed"
                                    ? "bg-green-lt"
                                    : "bg-blue-lt"
                                } rounded-pill px-2 py-1`}
                                style={{ fontSize: '0.7rem' }}
                              >
                                {quiz.status === "completed" ? "Completed" : "Active"}
                              </span>
                            </div>
                          </Nav.Link>
                        </Nav.Item>
                      ))}
                    </Nav>
                  </div>

                  {/* PAGINATION */}
                  {totalPages > 1 && (
                    <div className="border-top p-3 bg-white">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small fw-medium">
                          {currentPage} / {totalPages}
                        </span>

                        <ul className="pagination pagination-sm mb-0">
                          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <button
                              className="page-link shadow-none"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              <IconChevronLeft size={16} />
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                            <button
                              className="page-link shadow-none"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              <IconChevronRight size={16} />
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  )}
                </Col>

                {/* RIGHT CONTENT */}
                <Col sm={9}>
                  <Tab.Content
                    className="p-0 bg-white"
                    style={{ minHeight: "650px" }}
                  >
                    {allQuizzes.map((quiz) => {
                      const currentId = getQuizId(quiz);
                      return (
                        <Tab.Pane eventKey={currentId} key={currentId}>
                          {quiz.status === "completed" ? (
                            <div
                              className="d-flex flex-column justify-content-center align-items-center text-center p-5 animate-fade-in"
                              style={{ minHeight: "650px" }}
                            >
                              <div className="mb-4">
                                <div className="avatar avatar-xl bg-success-lt text-success rounded-circle shadow-sm" style={{ width: '80px', height: '80px' }}>
                                  <IconCheck size={40} stroke={3} />
                                </div>
                              </div>

                              <h2 className="fw-bold mb-3">{quiz.title}</h2>

                              <div className="mb-2 text-muted text-uppercase fw-bold" style={{ fontSize: "0.75rem", letterSpacing: "1.5px" }}>
                                Final Score
                              </div>

                              <div className="display-2 fw-bold text-success mb-4" style={{ letterSpacing: '-2px' }}>
                                {quiz.score || "0"}
                              </div>

                              <div className="card bg-light border-0 max-w-md mx-auto rounded-4 shadow-none">
                                <div className="card-body p-4">
                                  <p className="text-muted mb-0 lh-lg">
                                    Congratulations! You've successfully completed this assessment.
                                    The results have been recorded in your profile and are ready for review.
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : isQuizStarted ? (
                            <QuizActivePage
                              quizData={quiz}
                              onAnswerSelect={(qId, oId) =>
                                onAnswerSelect(qId, oId, currentId)
                              }
                              onComplete={() => {
                                setIsQuizStarted(false);
                                onFinalSubmit(currentId);
                              }}
                            />
                          ) : (
                            <QuizLandingPage
                              quizData={quiz}
                              onStart={() => setIsQuizStarted(true)}
                            />
                          )}
                        </Tab.Pane>
                      );
                    })}
                  </Tab.Content>
                </Col>
              </Row>
            </Tab.Container>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizDashboard;
